// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {StreamerModeGate} from '@app/features/streamer_mode/components/StreamerModeGate';
import StreamerMode from '@app/features/streamer_mode/state/StreamerMode';
import {AccountNavigationSection} from '@app/features/user/components/modals/tabs/account_security_tab/AccountNavigationSection';
import {AccountNestedPage} from '@app/features/user/components/modals/tabs/account_security_tab/AccountNestedPage';
import {AccountTabContent} from '@app/features/user/components/modals/tabs/account_security_tab/AccountTab';
import {DangerZoneTabContent} from '@app/features/user/components/modals/tabs/account_security_tab/DangerZoneTab';
import {SecurityTabContent} from '@app/features/user/components/modals/tabs/account_security_tab/SecurityTab';
import {isAccountNestedSettingsTab} from '@app/features/user/components/settings_utils/SettingsNavigationGroups';
import type {UserSettingsTabType} from '@app/features/user/components/settings_utils/SettingsSectionRegistry';
import Users from '@app/features/user/state/Users';
import WebAuthnCredentials from '@app/features/user/state/WebAuthnCredentials';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';

const ACCOUNT_DESCRIPTOR = msg({
	message: 'Account',
	comment: 'Short label in the account security tab. Keep it concise. Keep the tone plain and specific.',
});
const MANAGE_YOUR_EMAIL_PASSWORD_AND_ACCOUNT_SETTINGS_DESCRIPTOR = msg({
	message: 'Manage your email, password, and account settings',
	comment: 'Label in the account security tab. Keep the tone plain and specific.',
});
const SECURITY_DESCRIPTOR = msg({
	message: 'Security',
	comment: 'Short label in the account security tab. Keep it concise. Keep the tone plain and specific.',
});
const PROTECT_YOUR_ACCOUNT_WITH_TWO_FACTOR_AUTHENTICATION_AND_DESCRIPTOR = msg({
	message: 'Protect your account with two-factor authentication and passkeys',
	comment: 'Label in the account security tab. Keep the tone plain and specific.',
});
const DANGER_ZONE_DESCRIPTOR = msg({
	message: 'Danger zone',
	comment: 'Short label in the account security tab. Keep it concise. Keep the tone plain and specific.',
});
const IRREVERSIBLE_AND_DESTRUCTIVE_ACTIONS_DESCRIPTOR = msg({
	message: 'Irreversible and destructive actions',
	comment: 'Label in the account security tab. Keep the tone plain and specific.',
});
interface AccountSecurityTabProps {
	settingsTabType?: UserSettingsTabType;
}

const AccountSecurityTab: React.FC<AccountSecurityTabProps> = observer(({settingsTabType}) => {
	const {i18n} = useLingui();
	const user = Users.currentUser;
	const [showMaskedEmail, setShowMaskedEmail] = useState(false);
	if (!user) return null;
	if (isAccountNestedSettingsTab(settingsTabType)) {
		return <AccountNestedPage page={settingsTabType} data-flx="user.account-security-tab.account-nested-page" />;
	}
	if (StreamerMode.shouldHidePersonalInformation) {
		return (
			<SettingsTabContainer data-flx="user.account-security-tab.settings-tab-container.streamer-mode-gate">
				<SettingsTabContent data-flx="user.account-security-tab.settings-tab-content.streamer-mode-gate">
					<StreamerModeGate />
				</SettingsTabContent>
			</SettingsTabContainer>
		);
	}
	const isClaimed = user.isClaimed();
	const passkeys = isClaimed ? WebAuthnCredentials.credentials : [];
	return (
		<SettingsTabContainer data-flx="user.account-security-tab.settings-tab-container">
			<SettingsTabContent data-flx="user.account-security-tab.settings-tab-content">
				<AccountNavigationSection data-flx="user.account-security-tab.account-navigation-section" />
				{isClaimed && (
					<SettingsSection
						id="account"
						title={i18n._(ACCOUNT_DESCRIPTOR)}
						description={i18n._(MANAGE_YOUR_EMAIL_PASSWORD_AND_ACCOUNT_SETTINGS_DESCRIPTOR)}
						data-flx="user.account-security-tab.account"
					>
						<AccountTabContent
							user={user}
							isClaimed={isClaimed}
							showMaskedEmail={showMaskedEmail}
							setShowMaskedEmail={setShowMaskedEmail}
							data-flx="user.account-security-tab.account-tab-content"
						/>
					</SettingsSection>
				)}
				{isClaimed && (
					<SettingsSection
						id="security"
						title={i18n._(SECURITY_DESCRIPTOR)}
						description={i18n._(PROTECT_YOUR_ACCOUNT_WITH_TWO_FACTOR_AUTHENTICATION_AND_DESCRIPTOR)}
						data-flx="user.account-security-tab.security"
					>
						<SecurityTabContent
							user={user}
							isClaimed={isClaimed}
							passkeys={passkeys}
							data-flx="user.account-security-tab.security-tab-content"
						/>
					</SettingsSection>
				)}
				<SettingsSection
					id="danger_zone"
					title={i18n._(DANGER_ZONE_DESCRIPTOR)}
					description={i18n._(IRREVERSIBLE_AND_DESTRUCTIVE_ACTIONS_DESCRIPTOR)}
					data-flx="user.account-security-tab.danger-zone"
				>
					<DangerZoneTabContent
						user={user}
						isClaimed={isClaimed}
						data-flx="user.account-security-tab.danger-zone-tab-content"
					/>
				</SettingsSection>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});

export default AccountSecurityTab;
